import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { ZoomContext } from "../../zoom/zoom-context";
import { LobbyKnock, LobbySignal, SessionPhase, TelehealthRole } from "../types";
import {
  DENY_REKNOCK_SUPPRESS_MS,
  DENY_REMOVE_DELAY_MS,
} from "../telehealth-v2-constant";

interface LobbyApi {
  knocks: LobbyKnock[];
  /** UserIds the host has already admitted in this session. */
  admittedIds: number[];
  admit: (userId: number) => Promise<void>;
  deny: (userId: number) => Promise<void>;
  kick: (userId: number) => Promise<void>;
  knock: () => Promise<void>;
  /** Host-only: broadcast that the host is leaving so all guests disconnect. */
  announceHostLeft: () => Promise<void>;
}

const PROTOCOL_TAG = "th-v2";
const KNOCK_INTERVAL_MS = 4000;
/** Stale-knock window: a knock without a re-knock for this long is treated
 *  as the guest having closed their tab and is dropped from the host UI.
 *  Set well above KNOCK_INTERVAL_MS to absorb a missed re-knock or two. */
const KNOCK_STALE_MS = 12000;
const KNOCK_SWEEP_MS = 2000;

function encode(sig: LobbySignal): string {
  return `${PROTOCOL_TAG}:${JSON.stringify(sig)}`;
}

function decode(text: string): LobbySignal | null {
  if (!text || !text.startsWith(`${PROTOCOL_TAG}:`)) return null;
  try {
    return JSON.parse(text.slice(PROTOCOL_TAG.length + 1));
  } catch {
    return null;
  }
}

/**
 * Waiting-room layer on top of the Zoom Video SDK command channel.
 *
 * Host responsibilities
 *   - Listen for `knock` messages and surface them as a per-user admit prompt.
 *   - Track an `admittedIds` set so re-knocks from already-admitted guests are
 *     ignored (prevents the duplicate-admit-prompt issue).
 *
 * Guest responsibilities
 *   - Send a `knock` immediately on entering the lobby and re-send every few
 *     seconds in case the host wasn't connected yet.
 *   - Stop knocking the moment a `decision: admit` arrives, regardless of
 *     React phase updates landing later.
 */
export function useLobbyChannel(opts: {
  role: TelehealthRole;
  selfId?: number;
  phase: SessionPhase;
  setPhase: (p: SessionPhase) => void;
  /** Host-only: invoked when another host is already in the room. The
   *  caller should leave the session and surface an error to the user. */
  onDuplicateHost?: () => void;
  /** Guest-only: invoked when the host has left.
   *  - `final: true`  — host clicked End, the meeting is over: tear
   *                     down everything and close the tab/overlay.
   *  - `final: false` — host's tab closed or refreshed, they're likely
   *                     coming back: disconnect from the SDK session
   *                     but keep the overlay open so the guest can
   *                     re-knock once the host returns.
   *  The user-removed backstop fires with `final: false` (we can't know
   *  if a silent disconnect was a refresh or a real close). */
  onHostLeft?: (info: { final: boolean }) => void;
}): LobbyApi {
  const { role, selfId, phase, setPhase, onDuplicateHost, onHostLeft } = opts;
  const zmClient = useContext(ZoomContext);

  const [knocks, setKnocks] = useState<LobbyKnock[]>([]);
  const [admittedIds, setAdmittedIds] = useState<number[]>([]);
  const admittedRef = useRef<Set<number>>(new Set());
  // userId -> deny timestamp. Not folded into admittedRef: a denied guest
  // must be able to ask again, and admittedRef silently swallows knocks.
  const deniedAtRef = useRef<Map<number, number>>(new Map());
  const stopKnockRef = useRef<() => void>(() => {});
  // selfId via ref so the inbound-message handler always sees the latest
  // value even if Zoom dispatches the admit message before React has
  // committed setSelfId.
  const selfIdRef = useRef<number | undefined>(selfId);
  useEffect(() => {
    selfIdRef.current = selfId;
  }, [selfId]);
  // Latest onDuplicateHost via ref so the long-lived command-channel
  // listener doesn't need to re-bind on every render.
  const onDuplicateHostRef = useRef(onDuplicateHost);
  useEffect(() => {
    onDuplicateHostRef.current = onDuplicateHost;
  }, [onDuplicateHost]);
  const onHostLeftRef = useRef(onHostLeft);
  useEffect(() => {
    onHostLeftRef.current = onHostLeft;
  }, [onHostLeft]);
  // Once-per-session flag so a host-left signal followed by the host's
  // user-removed event doesn't fire the callback twice. Reset when the
  // guest re-enters the room (lobby/active) so a *future* host departure
  // after a guest rejoin still propagates.
  const hostLeftFiredRef = useRef(false);
  useEffect(() => {
    if (phase === "lobby" || phase === "active") {
      hostLeftFiredRef.current = false;
    }
  }, [phase]);
  // True once we've yielded to another host so we don't repeatedly fire
  // the callback on subsequent host-here messages.
  const yieldedRef = useRef(false);
  // Mirror admittedIds into a ref so the command-channel handlers and the
  // roster broadcast helper can read the latest list without re-binding.
  const admittedListRef = useRef<number[]>(admittedIds);
  useEffect(() => {
    admittedListRef.current = admittedIds;
  }, [admittedIds]);
  // Mirror knocks into a ref for the host rejoin catch-up timer below —
  // it needs the latest list at fire time, but we don't want the timer
  // to reset every time a new knock lands.
  const knocksRef = useRef<LobbyKnock[]>(knocks);
  useEffect(() => {
    knocksRef.current = knocks;
  }, [knocks]);
  // Once-per-session flag so the host rejoin catch-up only runs the first
  // time we enter the active phase.
  const rejoinCatchupDoneRef = useRef(false);

  const send = useCallback(
    async (sig: LobbySignal, userId?: number) => {
      const cmd = zmClient?.getCommandClient?.();
      if (!cmd) return;
      try {
        await cmd.send(encode(sig), userId);
      } catch {
        /* channel may not be ready yet */
      }
    },
    [zmClient]
  );

  /** Host-only: broadcast the current admitted-user list (host's own
   *  selfId + admitted guests) so admitted guests can hide unadmitted
   *  lobby users from their video stage. */
  const broadcastRoster = useCallback(() => {
    if (role !== "host") return;
    const me = selfIdRef.current;
    const list = admittedListRef.current;
    const withSelf =
      me !== undefined && !list.includes(me) ? [me, ...list] : list;
    void send({
      v: 1,
      type: "roster",
      admittedIds: withSelf,
      ts: Date.now(),
    });
  }, [role, send]);

  // ----- inbound message handling ----------------------------------------
  useEffect(() => {
    if (!zmClient) return;
    const onMessage = (payload: any) => {
      const sig = decode(payload?.text ?? "");
      if (!sig || sig.v !== 1) return;

      // HOST: collect knocks from guests still in the lobby.
      // The SDK types `senderId` as string, but `cmd.send(text, userId)`
      // expects a number — normalize once so admit/deny route correctly.
      // We track each knock's liveness with our own wall-clock timestamp,
      // refreshed on every re-knock. The stale-knock sweep below uses it
      // to drop guests who closed their tab without leaving cleanly.
      if (role === "host" && sig.type === "knock") {
        const senderId = Number(payload.senderId);
        if (!Number.isFinite(senderId)) return;
        if (admittedRef.current.has(senderId)) return;
        const now = Date.now();
        const deniedAt = deniedAtRef.current.get(senderId);
        if (deniedAt !== undefined) {
          if (now - deniedAt < DENY_REKNOCK_SUPPRESS_MS) return;
          // Window elapsed: this is a deliberate "ask again", so let it
          // through and prompt the host afresh.
          deniedAtRef.current.delete(senderId);
        }
        setKnocks((prev) => {
          const idx = prev.findIndex((k) => k.userId === senderId);
          if (idx >= 0) {
            const next = prev.slice();
            next[idx] = { ...next[idx], ts: now };
            return next;
          }
          return [
            ...prev,
            {
              userId: senderId,
              displayName: sig.displayName ?? payload.senderName ?? "Guest",
              ts: now,
            },
          ];
        });
        // Re-broadcast the roster so this newly-arrived guest immediately
        // learns who's already admitted (they may have joined after prior
        // admit broadcasts and missed them).
        broadcastRoster();
        return;
      }

      // GUEST: receive the host's admitted-roster broadcast and store it
      // locally so the stage filter can hide unadmitted lobby users.
      if (role === "guest" && sig.type === "roster") {
        const ids = Array.isArray(sig.admittedIds) ? sig.admittedIds : [];
        admittedListRef.current = ids;
        setAdmittedIds(ids);
        return;
      }

      // GUEST: react to host decisions. We broadcast (no `cmd.send` userId)
      // and target via the protocol's `targetUserId` field, because the
      // SDK's peer-routing userId is typed as `number` while the inbound
      // payload's `senderId` is `string` — relying on it drops messages.
      if (role === "guest" && sig.type === "decision") {
        const me = selfIdRef.current;
        if (sig.targetUserId !== undefined && sig.targetUserId !== me) return;
        stopKnockRef.current();
        if (sig.decision === "admit") setPhase("active");
        else if (sig.decision === "deny") setPhase("denied");
        return;
      }

      if (role === "guest" && sig.type === "kick") {
        const me = selfIdRef.current;
        if (sig.targetUserId !== undefined && sig.targetUserId !== me) return;
        setPhase("ended");
      }

      // GUEST: host announced they're leaving. Disconnect the guest so the
      // session ends for everyone the moment the host clicks Leave, rather
      // than waiting ~30s for Zoom's user-removed event.
      if (role === "guest" && sig.type === "host-left") {
        if (hostLeftFiredRef.current) return;
        hostLeftFiredRef.current = true;
        onHostLeftRef.current?.({ final: sig.final === true });
        return;
      }

      // Multi-host detection. Both hosts broadcast `host-here` on becoming
      // active; each receiver runs the same tiebreak so the conclusion is
      // symmetric without an extra round-trip. Lower userId wins because
      // Zoom assigns userIds in join order — the host already in the room
      // has the lower id, the newcomer has the higher id and yields.
      if (role === "host" && sig.type === "host-here") {
        const senderId = Number(payload.senderId);
        if (!Number.isFinite(senderId)) return;
        const me = selfIdRef.current;
        if (me === undefined || senderId === me) return;
        if (yieldedRef.current) return;
        if (me > senderId) {
          yieldedRef.current = true;
          onDuplicateHostRef.current?.();
        }
        return;
      }
    };
    zmClient.on("command-channel-message", onMessage);
    return () => {
      zmClient.off("command-channel-message", onMessage);
    };
  }, [zmClient, role, setPhase, broadcastRoster]);

  // ----- host: announce presence so a duplicate host can yield -----------
  useEffect(() => {
    if (role !== "host" || phase !== "active" || !selfId) return;
    // A short burst rather than a single send: the second host may not be
    // listening yet when the first one announces, and vice-versa. Three
    // sends over ~1.5s reliably reaches both peers without flooding.
    let i = 0;
    const fire = () =>
      send({
        v: 1,
        type: "host-here",
        ts: Date.now(),
      });
    fire();
    const interval = window.setInterval(() => {
      i += 1;
      fire();
      if (i >= 2) window.clearInterval(interval);
    }, 700);
    return () => window.clearInterval(interval);
  }, [role, phase, selfId, send]);

  // Existing host re-announces every time a new participant joins, so a
  // newcomer that happens to also be a host receives a host-here from us
  // and yields per the lower-userId tiebreak. Without this, the existing
  // host's initial burst already played out before the newcomer connected
  // and they never heard us.
  useEffect(() => {
    if (!zmClient) return;
    if (role !== "host") return;
    const onUserAdded = () => {
      void send({
        v: 1,
        type: "host-here",
        ts: Date.now(),
      });
    };
    zmClient.on("user-added", onUserAdded);
    return () => zmClient.off("user-added", onUserAdded);
  }, [zmClient, role, send]);

  // ----- guest auto-knock while in the lobby -----------------------------
  useEffect(() => {
    if (role !== "guest") return;
    if (phase !== "lobby" || !selfId) {
      stopKnockRef.current();
      return;
    }

    let cancelled = false;
    const me = zmClient?.getCurrentUserInfo?.();
    const knockOnce = () =>
      send({
        v: 1,
        type: "knock",
        displayName: me?.displayName ?? "Guest",
        ts: Date.now(),
      });

    knockOnce();
    const interval = window.setInterval(() => {
      if (!cancelled) knockOnce();
    }, KNOCK_INTERVAL_MS);
    stopKnockRef.current = () => {
      cancelled = true;
      window.clearInterval(interval);
    };
    return () => stopKnockRef.current();
  }, [role, phase, selfId, zmClient, send]);

  // ----- prune stale knocks + admit list when users leave -----------------
  useEffect(() => {
    if (!zmClient) return;
    const onRemoved = (payload: any) => {
      const list = Array.isArray(payload) ? payload : [payload].filter(Boolean);
      const removedIds = list
        .map((u: any) => u?.userId)
        .filter((v: any) => typeof v === "number");
      if (removedIds.length === 0) return;
      setKnocks((prev) => prev.filter((k) => !removedIds.includes(k.userId)));
      // Drop departed users from the admitted list too so the broadcast
      // effect re-syncs the smaller roster to remaining guests.
      setAdmittedIds((prev) => {
        const next = prev.filter((id) => !removedIds.includes(id));
        return next.length === prev.length ? prev : next;
      });

      // Backstop for an unclean host disconnect: if the removed user was the
      // host and we're a guest, end the session for ourselves. The clean
      // path uses the host-left signal above; this fires when the host's
      // tab/network died before they could broadcast. We treat it as a
      // non-final departure (final: false) — the host might still come
      // back from a refresh, and a real End-button click would have been
      // caught by the host-left signal above first.
      if (role === "guest" && !hostLeftFiredRef.current) {
        const hostRemoved = list.some((u: any) => !!u?.isHost);
        if (hostRemoved) {
          hostLeftFiredRef.current = true;
          onHostLeftRef.current?.({ final: false });
        }
      }
    };
    zmClient.on("user-removed", onRemoved);
    return () => zmClient.off("user-removed", onRemoved);
  }, [zmClient, role]);

  // Host: rebroadcast the admitted roster whenever it (or selfId) changes.
  // Other admitted guests pick this up and update their local allowlist so
  // an unadmitted lobby user no longer appears in their video stage.
  useEffect(() => {
    if (role !== "host") return;
    if (selfId === undefined) return;
    broadcastRoster();
  }, [role, selfId, admittedIds, broadcastRoster]);

  // Host: heartbeat re-broadcast. Change-driven broadcasts above can be
  // missed by a guest whose WebSocket hiccuped at exactly the wrong
  // moment (e.g. a `user-removed` event lost on one peer but received on
  // another, leaving a stale entry in the guest's admittedIds). A small
  // periodic re-broadcast self-corrects these drifts within a few
  // seconds without flooding the channel — the payload is tiny and
  // idempotent for guests already in sync.
  useEffect(() => {
    if (role !== "host") return;
    if (selfId === undefined) return;
    const id = window.setInterval(() => {
      broadcastRoster();
    }, 8000);
    return () => window.clearInterval(id);
  }, [role, selfId, broadcastRoster]);

  // Host rejoin catch-up. When a new host opens the rejoin URL into a
  // meeting that was already running, our admittedIds starts empty — so
  // the next roster broadcast would erase admitted guests from each
  // other's view. Wait briefly for in-flight knocks to land (knock loop
  // is 4s), then treat anyone in the SDK roster who isn't currently
  // knocking as already admitted. The next broadcast carries them along.
  useEffect(() => {
    if (role !== "host" || phase !== "active" || selfId === undefined) return;
    if (rejoinCatchupDoneRef.current) return;
    const t = window.setTimeout(() => {
      rejoinCatchupDoneRef.current = true;
      const me = selfIdRef.current;
      const all = (() => {
        try {
          return zmClient?.getAllUser?.() ?? [];
        } catch {
          return [];
        }
      })();
      const knockingIds = new Set(knocksRef.current.map((k) => k.userId));
      const candidates = all
        .map((u: any) => Number(u?.userId))
        .filter((id: number) => Number.isFinite(id))
        .filter((id: number) => id !== me)
        .filter((id: number) => !knockingIds.has(id));
      if (candidates.length === 0) return;
      setAdmittedIds((prev) => {
        const set = new Set(prev);
        let changed = false;
        for (const id of candidates) {
          if (!set.has(id)) {
            set.add(id);
            admittedRef.current.add(id);
            changed = true;
          }
        }
        return changed ? Array.from(set) : prev;
      });
    }, 5000);
    return () => window.clearTimeout(t);
  }, [role, phase, selfId, zmClient]);

  // Backstop for a tab close: Zoom's `user-removed` can take ~30s to fire
  // for an unclean disconnect. The guest's knock loop stops immediately
  // though, so a missing re-knock is the fastest signal. Sweep periodically
  // and drop knocks whose ts hasn't been refreshed.
  useEffect(() => {
    if (role !== "host") return;
    const id = window.setInterval(() => {
      const cutoff = Date.now() - KNOCK_STALE_MS;
      setKnocks((prev) => {
        if (!prev.some((k) => k.ts < cutoff)) return prev;
        return prev.filter((k) => k.ts >= cutoff);
      });
    }, KNOCK_SWEEP_MS);
    return () => window.clearInterval(id);
  }, [role]);

  const knock = useCallback(async () => {
    const me = zmClient?.getCurrentUserInfo?.();
    await send({
      v: 1,
      type: "knock",
      displayName: me?.displayName ?? "Guest",
      ts: Date.now(),
    });
  }, [zmClient, send]);

  // All host-side commands are broadcast (no userId). Each message carries
  // its own `targetUserId` so guests can filter — this is more reliable than
  // depending on Zoom's peer-to-peer userId routing.
  const admit = useCallback(
    async (userId: number) => {
      const id = Number(userId);
      admittedRef.current.add(id);
      setAdmittedIds((p) => (p.includes(id) ? p : [...p, id]));
      setKnocks((prev) => prev.filter((k) => k.userId !== id));
      await send({
        v: 1,
        type: "decision",
        decision: "admit",
        targetUserId: id,
        ts: Date.now(),
      });
    },
    [send]
  );

  const deny = useCallback(
    async (userId: number) => {
      const id = Number(userId);
      deniedAtRef.current.set(id, Date.now());
      setKnocks((prev) => prev.filter((k) => k.userId !== id));
      await send({
        v: 1,
        type: "decision",
        decision: "deny",
        targetUserId: id,
        ts: Date.now(),
      });
      // removeUser closes the guest's connection; without this gap the
      // Closed event can beat the decision message and the guest lands on
      // the generic "disconnected" prompt instead of the declined screen.
      await new Promise((r) => setTimeout(r, DENY_REMOVE_DELAY_MS));
      try {
        await zmClient?.removeUser?.(id);
      } catch {
        /* ignore */
      }
    },
    [send, zmClient]
  );

  const kick = useCallback(
    async (userId: number) => {
      const id = Number(userId);
      admittedRef.current.delete(id);
      setAdmittedIds((p) => p.filter((x) => x !== id));
      await send({
        v: 1,
        type: "kick",
        targetUserId: id,
        ts: Date.now(),
      });
      try {
        await zmClient?.removeUser?.(id);
      } catch {
        /* ignore */
      }
    },
    [send, zmClient]
  );

  const announceHostLeft = useCallback(async () => {
    if (role !== "host") return;
    // Burst of three sends: the command channel can drop a single message
    // when the sender's connection is already tearing down, and we want
    // every guest to receive at least one. A short total window keeps the
    // host's leave click feeling responsive. `final: true` tells guests
    // this is the End button — they should fully tear down the call.
    const fire = () =>
      send({
        v: 1,
        type: "host-left",
        final: true,
        ts: Date.now(),
      });
    await fire();
    void fire();
    void fire();
  }, [role, send]);

  // Fire host-left on tab close / refresh too, not just on the Leave button.
  // Without this, guests have to wait for Zoom's user-removed heartbeat
  // (~30s) before they realise the host's gone — by which time they're
  // staring at an empty meeting. The sends are fire-and-forget because the
  // tab is unloading and we can't await; the burst gives them the best
  // chance of being flushed onto the wire before the connection tears down.
  // `final: false` so the guest disconnects from the SDK session but
  // keeps the overlay open — a refresh will come back, and a real tab
  // close still leaves the guest with a Rejoin affordance.
  useEffect(() => {
    if (role !== "host") return;
    const onPageHide = () => {
      const fire = () =>
        void send({
          v: 1,
          type: "host-left",
          final: false,
          ts: Date.now(),
        });
      fire();
      fire();
      fire();
    };
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("beforeunload", onPageHide);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("beforeunload", onPageHide);
    };
  }, [role, send]);

  return {
    knocks,
    admittedIds,
    admit,
    deny,
    kick,
    knock,
    announceHostLeft,
  };
}
